import { lazy, Suspense, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import RequireAuth from './components/RequireAuth';
import store from './store/store';
import { SidebarSlotProvider } from './components/SidebarSlotContext';
import AuthProvider from './provider/AuthContext';

const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const BluetoothClassicListProbes = lazy(() => import('./pages/BluetoothClassicProbePage'));
const DeviceDashboard = lazy(() => import('./pages/DeviceDashboard'));
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RuntimeSessionsDetailPage = lazy(() => import('./pages/RuntimeSessionsDetailPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ProbeReport = lazy(() => import('./pages/ProbeReport'));
const WifiProbePage = lazy(() => import('./pages/WifiProbePage'));

// const socket = io(process.env.REACT_APP_SOCKET_URL, {
//   // transports: ["websocket", "polling"], // ✅ fallback to polling if websocket fails
// });

function AppShell() {
  const location = useLocation();
  const [routeLoading, setRouteLoading] = useState(false);

  useEffect(() => {
    setRouteLoading(true);
    const t = setTimeout(() => setRouteLoading(false), 300);
    return () => clearTimeout(t);
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname;
    const match = path.match(/^\/device\/([^/]+)(?:\/([^/]+))?/);
    if (match) {
      // URL param is our canonical device identifier (screenId/hostname), not the legacy DB `deviceId` column.
      const screenId = match[1];
      const section = match[2];
      if (section === 'dashboard') {
        document.title = `${screenId} Dashboard - Minute Marketing`;
      } else if (section === 'wifi') {
        document.title = `${screenId} Wi-Fi Probe - Minute Marketing`;
      } else if (section === 'bluetooth') {
        document.title = `${screenId} Bluetooth Probe - Minute Marketing`;
      } else if (section === 'runtime') {
        document.title = `${screenId} Runtime Details - Minute Marketing`;
      } else {
        document.title = `${screenId} - Minute Marketing`;
      }
      return;
    }
    if (path === '/') {
      document.title = 'Dashboard - Minute Marketing';
      return;
    }
    if (path.startsWith('/alerts')) {
      document.title = 'Alerts & History - Minute Marketing';
      return;
    }
    if (path.startsWith('/reports')) {
      document.title = 'Report - Minute Marketing';
      return;
    }
    document.title = 'Minute Marketing';
  }, [location.pathname]);

  const isLoginRoute = location.pathname === '/login';

  return (
    <div className='app-shell'>
      {routeLoading && !isLoginRoute && (
        <div className='route-loader'>
          <div className='loader-stack'>
            <div className='spinner-border text-primary' role='status' aria-label='Loading page' />
            <div className='text-muted small'>Loading, please wait...</div>
          </div>
        </div>
      )}
      {!isLoginRoute && <Navbar />}
      <div className={`layout ${isLoginRoute ? 'no-sidebar' : ''}`}>
        {!isLoginRoute && <Sidebar />}
        <main className={`page-content ${isLoginRoute ? 'page-content-auth' : ''}`}>
          <Suspense
            fallback={
              <div className='route-loader'>
                <div className='loader-stack'>
                  <div className='spinner-border text-primary' role='status' aria-label='Loading page' />
                  <div className='text-muted small'>Loading, please wait...</div>
                </div>
              </div>
            }
          >
            <Routes>
              <Route path='/login' element={<LoginPage />} />
              <Route
                path='/'
                element={
                  <RequireAuth>
                    <HomePage />
                  </RequireAuth>
                }
              />
              <Route
                path='/alerts'
                element={
                  <RequireAuth>
                    <AlertsPage />
                  </RequireAuth>
                }
              />
              <Route
                path='/reports'
                element={
                  <RequireAuth>
                    <ProbeReport />
                  </RequireAuth>
                }
              />
              <Route
                path='/device/:id/dashboard'
                element={
                  <RequireAuth>
                    <DeviceDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path='/device/:id/wifi'
                element={
                  <RequireAuth>
                    <WifiProbePage />
                  </RequireAuth>
                }
              />
              <Route
                path='/device/:id/bluetooth'
                element={
                  <RequireAuth>
                    <BluetoothClassicListProbes />
                  </RequireAuth>
                }
              />
              <Route
                path='/device/:id/runtime'
                element={
                  <RequireAuth>
                    <RuntimeSessionsDetailPage />
                  </RequireAuth>
                }
              />
              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <BrowserRouter>
          <SidebarSlotProvider>
            <AppShell />
          </SidebarSlotProvider>
        </BrowserRouter>
      </AuthProvider>
    </Provider>
  );
}

export default App;
